import api from './api';

/**
 * Field Visibility API utilities
 * Handles field-level privacy controls for catalog items
 */

export type FieldVisibility = 'public' | 'private';

export interface FieldVisibilityUpdate {
  fieldPath: string;
  visibility: FieldVisibility;
}

export interface BulkFieldVisibilityUpdate {
  fields: FieldVisibilityUpdate[];
}

export interface FieldVisibilityResponse {
  catalogItemId: string;
  fieldSettings: Record<string, FieldVisibility>;
  updatedAt: string | null;
}

// Section names that map to groups of fields
export type SectionName =
  | 'describe'
  | 'media'
  | 'measure'
  | 'catalogKeywords'
  | 'provenance'
  | 'additionalInfo'
  | 'upload';

// Field paths for each section (must match backend SECTION_FIELDS)
export const SECTION_FIELDS: Record<SectionName, string[]> = {
  describe: [
    'describe.creators',
    'describe.title',
    'describe.titleLocation',
    'describe.titleAsWritten',
    'describe.alternativeTitles',
    'describe.signature',
    'describe.signatureType',
    'describe.signatureLocation',
    'describe.creationDate',
    'describe.locationOfDate',
    'describe.dateDisplay',
    'describe.creationYear',
    'describe.notes',
  ],
  media: [
    'describe.mediaDescription',
    'describe.workType',
    'describe.techniques',
    'describe.materials',
    'describe.substrate',
    'describe.mediaAdditionalComments',
  ],
  measure: [
    'measure.dimensions',
    'measure.height',
    'measure.width',
    'measure.depth',
    'measure.unit',
    'measure.weight',
    'measure.weightUnit',
    'measure.condition',
    'measure.substrateDimensions',
    'measure.notes',
  ],
  catalogKeywords: [
    'catalogKeywords.aliCatalogNumber',
    'catalogKeywords.customNumbers',
    'catalogKeywords.keywords',
    'catalogKeywords.notes',
  ],
  provenance: [
    'provenance.provenanceRecords',
    'provenance.exhibitions',
    'provenance.publications',
    'provenance.notes',
  ],
  additionalInfo: [
    'additionalInfo.relatedWorks',
    'additionalInfo.publicNotes',
    'additionalInfo.physicalLocation',
    'additionalInfo.estimatedValue',
    // Note: privateNotes intentionally excluded - always private
  ],
  upload: [
    'upload.images',
    'upload.coverImage',
  ],
};

/**
 * Get all field visibility settings for a catalog item
 */
export const getFieldVisibility = async (
  catalogItemId: string,
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const params = ownerId ? { owner_id: ownerId } : undefined;
  const response = await api.get(`/api/field-visibility/${catalogItemId}`, { params });
  return response.data;
};

/**
 * Update visibility for a single field
 */
export const updateFieldVisibility = async (
  catalogItemId: string,
  fieldPath: string,
  visibility: FieldVisibility,
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
  const response = await api.put(`/api/field-visibility/${catalogItemId}/field`, {
    fieldPath,
    visibility,
  }, config);
  return response.data;
};

/**
 * Update visibility for multiple fields at once
 */
export const bulkUpdateFieldVisibility = async (
  catalogItemId: string,
  fields: FieldVisibilityUpdate[],
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
  const response = await api.put(`/api/field-visibility/${catalogItemId}/bulk`, {
    fields,
  }, config);
  return response.data;
};

/**
 * Make all fields in a section public
 */
export const makeSectionPublic = async (
  catalogItemId: string,
  section: SectionName,
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
  const response = await api.put(
    `/api/field-visibility/${catalogItemId}/section/${section}/public`,
    undefined,
    config
  );
  return response.data;
};

/**
 * Make all fields in a section private
 */
export const makeSectionPrivate = async (
  catalogItemId: string,
  section: SectionName,
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
  const response = await api.put(
    `/api/field-visibility/${catalogItemId}/section/${section}/private`,
    undefined,
    config
  );
  return response.data;
};

/**
 * Check if a specific field is public based on settings
 * Defaults to private if not explicitly set
 */
export const isFieldPublic = (
  fieldSettings: Record<string, FieldVisibility>,
  fieldPath: string
): boolean => {
  return fieldSettings[fieldPath] === 'public';
};

/**
 * Fields that must always remain private regardless of bulk privacy actions.
 * These fields are excluded from "Make All Public" operations and are not
 * considered when determining whether a section is fully public.
 *
 * Must stay in sync with backend `src/models/field_visibility.py`
 * ALWAYS_PRIVATE_FIELDS and with `scripts/fix-visibility-sync-1108.py`.
 */
export const ALWAYS_PRIVATE_FIELDS: ReadonlySet<string> = new Set([
  'additionalInfo.estimatedValue',
  'additionalInfo.physicalLocation',
  // Per-image uploadedAt (#1112) — internal-only legal-proof timestamp.
  // Scrubbed from public responses server-side; listed here for parity with
  // the backend policy set. Not in SECTION_FIELDS, so the filter is a no-op
  // in practice, but keeping it in both places prevents future drift.
  'upload.images.uploadedAt',
]);

/**
 * Check if all fields in a section are public.
 * Fields listed in ALWAYS_PRIVATE_FIELDS are excluded from this check —
 * a section is considered "All Public" even if those fields remain private.
 */
export const isSectionPublic = (
  fieldSettings: Record<string, FieldVisibility>,
  section: SectionName
): boolean => {
  const sectionFields = SECTION_FIELDS[section];
  return sectionFields
    .filter((fieldPath) => !ALWAYS_PRIVATE_FIELDS.has(fieldPath))
    .every((fieldPath) => fieldSettings[fieldPath] === 'public');
};

/**
 * Check if all fields in a section are private (or not set).
 *
 * Fields listed in ALWAYS_PRIVATE_FIELDS are excluded, symmetric with
 * isSectionPublic. Stuck values at those paths (e.g. legacy "public"
 * entries from pre-#852 data) are not user-controllable and must not
 * block the section from registering as private — otherwise the
 * ObjectPrivacyControls chip falls through to "Mixed" on objects the
 * user has never made public (#1108).
 */
export const isSectionPrivate = (
  fieldSettings: Record<string, FieldVisibility>,
  section: SectionName
): boolean => {
  const sectionFields = SECTION_FIELDS[section];
  return sectionFields
    .filter((fieldPath) => !ALWAYS_PRIVATE_FIELDS.has(fieldPath))
    .every(
      (fieldPath) => !fieldSettings[fieldPath] || fieldSettings[fieldPath] === 'private'
    );
};

const ALL_SECTIONS: readonly SectionName[] = [
  'describe', 'media', 'measure', 'catalogKeywords', 'provenance', 'additionalInfo', 'upload',
] as const;

/**
 * Check if all fields across all sections are public.
 * Always-private fields are excluded from the check.
 */
export const isObjectFieldsPublic = (
  fieldSettings: Record<string, FieldVisibility>
): boolean => ALL_SECTIONS.every((section) => isSectionPublic(fieldSettings, section));

/**
 * Check if all fields across all sections are private (or not set).
 */
export const isObjectFieldsPrivate = (
  fieldSettings: Record<string, FieldVisibility>
): boolean => ALL_SECTIONS.every((section) => isSectionPrivate(fieldSettings, section));

/**
 * Make all fields across all sections public (except always-private fields).
 */
export const makeObjectPublic = async (
  catalogItemId: string,
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
  const response = await api.put(
    `/api/field-visibility/${catalogItemId}/object/public`,
    undefined,
    config
  );
  return response.data;
};

/**
 * Make all fields across all sections private.
 */
export const makeObjectPrivate = async (
  catalogItemId: string,
  ownerId?: string | null
): Promise<FieldVisibilityResponse> => {
  const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
  const response = await api.put(
    `/api/field-visibility/${catalogItemId}/object/private`,
    undefined,
    config
  );
  return response.data;
};
